import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ThumbsUp, ThumbsDown, AlertCircle, ArrowUpDown } from 'lucide-react';

function SentimentComparison({ competitors }) {
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = high to low, 'asc' = low to high

  // Prepare data for sentiment chart
  const sentimentData = React.useMemo(() => {
    if (!competitors) return [];
    const data = competitors.map(comp => ({
      name: comp.name,
      sentiment: Math.round((comp.sentiment_score || 0) * 100),
      fullScore: comp.sentiment_score || 0,
    }));
    return sortOrder === 'desc' 
      ? data.sort((a, b) => b.sentiment - a.sentiment)
      : data.sort((a, b) => a.sentiment - b.sentiment);
  }, [competitors, sortOrder]);

  const getSentimentColor = (score) => {
    if (score >= 85) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#374151' }}>
            {data.name}
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: getSentimentColor(data.sentiment) }}>
            Sentiment: {data.sentiment}%
          </p>
        </div>
      );
    }
    return null;
  };

  if (!competitors || competitors.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: '#6b7280',
      }}>
        <p>No sentiment data available</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
      }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
          Sentiment Comparison
        </h3>
        <button
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 12px',
            fontSize: '12px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <ArrowUpDown size={14} />
          {sortOrder === 'desc' ? 'High→Low' : 'Low→High'}
        </button>
      </div>

      {/* Sentiment Bar Chart */}
      <div style={{ height: '250px', marginBottom: '24px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sentimentData} layout="vertical" margin={{ left: 80 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              stroke="#6b7280"
              fontSize={12}
            />
            <YAxis 
              type="category" 
              dataKey="name" 
              stroke="#374151"
              fontSize={12}
              width={70}
              tickFormatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="sentiment" radius={[0, 4, 4, 0]}>
              {sentimentData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getSentimentColor(entry.sentiment)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Competitor Sentiment Details */}
      <div style={{ display: 'grid', gap: '12px' }}>
        {competitors
          .sort((a, b) => {
            const diff = (b.sentiment_score || 0) - (a.sentiment_score || 0);
            return sortOrder === 'desc' ? diff : -diff;
          })
          .map((comp) => {
            const score = Math.round((comp.sentiment_score || 0) * 100);
            const isPositive = score >= 85;
            const isNeutral = score >= 50 && score < 85;
            
            return (
              <div
                key={comp.name}
                style={{
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getSentimentColor(score)}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                    {comp.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isPositive ? <ThumbsUp size={16} color="#10b981" /> : 
                     isNeutral ? <AlertCircle size={16} color="#f59e0b" /> : 
                     <ThumbsDown size={16} color="#ef4444" />}
                    <span style={{ fontSize: '14px', fontWeight: '600', color: getSentimentColor(score) }}>
                      {score}%
                    </span>
                  </div>
                </div>
                
                {comp.review_summary && (
                  <p style={{ margin: '0', fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
                    {comp.review_summary}
                  </p>
                )}

                {/* Price indicator */}
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Price: <span style={{ fontWeight: '600', color: '#374151' }}>
                    ${comp.current_price?.toFixed(2) || 'N/A'}
                  </span>
                </div>
              </div>
            );
          })}
      </div>

      {/* Legend */}
      <div style={{ 
        marginTop: '16px', 
        paddingTop: '16px', 
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        gap: '20px',
        justifyContent: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#10b981' }} />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Positive (85%+)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#f59e0b' }} />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Neutral (50-84%)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#ef4444' }} />
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Negative (&lt;50%)</span>
        </div>
      </div>
    </div>
  );
}

export default SentimentComparison;
